import type { CollectibleId } from "@/services/collectibles";
import type { CategoryId } from "@/services/collection/categories";
import { isValidCategoryId } from "@/services/collection/categories/categoriesHelpers";
import { getEnumKeyByValue, isStringInEnum } from "@/utilities";
import { readonly } from "vue";
import { useRoute, type LocationQueryRaw } from "vue-router";
import { useRouter } from "vue-router";

export const DefaultPerPage = 60;

export enum Sort {
  PriceAsc = "priceMinToMax",
  PriceDesc = "priceMaxToMin",
  DateDesc = "marketplaceListedNewestFirst",
  DateAsc = "marketplaceListedOldestFirst",
  NameAsc = "alphabetAsc",
  NameDesc = "alphabetDesc",
  Likes = "likesCountDesc",
  LikesRecent = "recentLikesCountDesc",
  Relevance = "relevance",
}

export type Model = {
  page: number;
  perPage: number;
  categoryId: CategoryId[];
  sort: Sort;
  fulltext?: string;
  collectibleId?: CollectibleId;
  priceFrom?: number;
  priceTo?: number;
};

const model = ref<Model>({
  page: 0,
  perPage: DefaultPerPage,
  categoryId: [],
  sort: Sort.DateDesc,
  fulltext: undefined,
  collectibleId: undefined,
  priceFrom: undefined,
  priceTo: undefined,
});

type Options = Partial<{
  syncWithUrl: boolean;
}>;

export function useMarketplaceFilter(options: Options = {}) {
  const config = {
    syncWithUrl: true,
    ...options,
  };

  const router = useRouter();
  const route = useRoute();

  const isPageUpdateDisabled = ref(false);

  /**
   * ========================================
   * Filter fields
   * ========================================
   */

  const page = computed({
    get: () => model.value.page,
    set: (val: number) => {
      if (!isPageUpdateDisabled.value) {
        model.value.page = val;
      }
    },
  });

  const perPage = computed({
    get: () => model.value.perPage,
    set: (val: number) => (model.value.perPage = val),
  });

  const categoryId = computed({
    get: () => model.value.categoryId,
    set: (val: CategoryId[]) => {
      model.value.categoryId.splice(0);
      val.forEach((id) => model.value.categoryId.push(id));
      model.value.page = 0;
    },
  });

  const fulltext = computed({
    get: () => model.value.fulltext,
    set: (val: string) => {
      model.value.fulltext = val === "" ? undefined : val;
      model.value.page = 0;
    },
  });

  const collectibleId = computed({
    get: () => model.value.collectibleId,
    set: (val: CollectibleId | undefined) => {
      model.value.collectibleId = val;
      model.value.page = 0;
    },
  });

  const priceFrom = computed({
    get: () => model.value.priceFrom,
    set: (val: number | undefined) => {
      model.value.priceFrom = val;
      model.value.page = 0;
    },
  });

  const priceTo = computed({
    get: () => model.value.priceTo,
    set: (val: number | undefined) => {
      model.value.priceTo = val;
      model.value.page = 0;
    },
  });

  const sort = computed({
    get: () => model.value.sort,
    set: (val: Sort) => {
      model.value.sort = val;
      model.value.page = 0;
    },
  });

  /**
   * ========================================
   * Additional computed filter info
   * ========================================
   */

  const activeFilterCount = computed(() => {
    let count = 0;

    if (categoryId.value.length > 0) {
      count++;
    }

    if (fulltext.value !== undefined) {
      count++;
    }

    if (collectibleId.value !== undefined) {
      count++;
    }

    if (priceFrom.value !== undefined || priceTo.value !== undefined) {
      count++;
    }

    return count;
  });

  /**
   * ========================================
   * Filter manipulation
   * ========================================
   */

  const updateUrlByModel = (model: Model) => {
    const query: LocationQueryRaw = {};
    query.page = model.page;
    query.perPage = model.perPage;
    query.fulltext = model.fulltext;
    query.categoryId = model.categoryId;
    query.sort = model.sort;
    query.collectibleId = model.collectibleId;
    query.priceFrom = model.priceFrom;
    query.priceTo = model.priceTo;
    router.replace({ query });
  };

  watch(
    model,
    (updatedModel) => {
      if (config.syncWithUrl) {
        updateUrlByModel(updatedModel);
      }
    },
    { deep: true }
  );

  const updateModelByUrl = () => {
    isPageUpdateDisabled.value = true;

    const query = route.query;

    const queryPerPage = Number(query.perPage);
    if (isNaN(queryPerPage) || queryPerPage <= 0) {
      perPage.value = DefaultPerPage;
    } else {
      perPage.value = queryPerPage;
    }

    const querySort = query.sort?.toString() ?? "";
    sort.value = isStringInEnum(querySort, Sort) ? getEnumKeyByValue(querySort, Sort) : Sort.DateDesc;

    fulltext.value = query.fulltext?.toString() ?? "";

    collectibleId.value = Number(query.collectibleId) || undefined;

    if (Array.isArray(query.categoryId)) {
      categoryId.value = query.categoryId.map((id) => (isValidCategoryId(Number(id)) ? Number(id) : null)).filter((id) => id !== null);
    } else {
      const id = Number(query.categoryId);
      if (isValidCategoryId(id)) {
        categoryId.value = [id];
      } else {
        categoryId.value = [];
      }
    }

    priceFrom.value = Number(query.priceFrom) || undefined;
    priceTo.value = Number(query.priceTo) || undefined;

    isPageUpdateDisabled.value = false;
    page.value = Number(query.page) || 0;
  };

  const reset = () => {
    model.value = {
      page: 0,
      perPage: DefaultPerPage,
      categoryId: [],
      sort: Sort.DateDesc,
      fulltext: undefined,
      collectibleId: undefined,
      priceTo: undefined,
      priceFrom: undefined,
    };
  };

  /**
   * ========================================
   * Helpers
   * ========================================
   */

  const buildApiQuery = () => ({
    page: page.value,
    perPage: perPage.value,
    categoryId: categoryId.value.length > 0 ? categoryId.value : undefined,
    sort: model.value.sort,
    fulltext: fulltext.value,
    collectibleId: collectibleId.value,
    minPrice: priceFrom.value,
    maxPrice: priceTo.value,
  });

  return {
    filter: readonly(model),

    page,
    perPage,
    fulltext,
    categoryId,
    sort,
    collectibleId,
    priceFrom,
    priceTo,

    activeFilterCount: readonly(activeFilterCount),

    // Helpers:
    buildApiQuery,
    updateModelByUrl,
    reset,
  };
}
